import React, { useEffect, useState } from 'react'

import ChatService from '../../../services/ChatService'
import ChatRoom from '../../chat/ChatRoom'
import LoadingSpinner from '../../LoadingSpinner'

const CaseMessagesTab = ({ caseNumber, user, settings }) => {
  // Note: caseNumber is passed from CasePage, but our API uses caseId (Guid).
  // We might need to fetch the Case ID or assume we have it.
  // Actually, CasePage fetches `client` but doesn't explicitly pass the full case object with ID to tabs?
  // Wait, CasesService.get(caseNumber) returns the case object.

  // Changing approach: fetch threads and filter by `caseNumber` if the thread has it?
  // The ChatThread model has `CaseId` (Guid), not `CaseNumber`.
  // I need the Case GUID.
  // CasePage does `CasesService.get(caseNumber)` -> `r` is the case.
  // But `CasePage` only sets `client` state. It doesn't pass the full case object to tabs!
  // It passes `caseNumber` (string).

  // I will need to fetch the Case ID using the caseNumber.
  // Or I can update `CasePage.js` to pass the full case object.

  const [threads, setThreads] = useState([])
  const [selectedThread, setSelectedThread] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [newSubject, setNewSubject] = useState('')

  useEffect(() => {
    if (caseNumber) {
      loadThreads(parseInt(caseNumber))
    }
  }, [caseNumber])

  const loadThreads = async cNum => {
    try {
      const data = await ChatService.getThreads(1, 200)
      // Filter by CaseNumber from the array response
      const caseThreads = (Array.isArray(data) ? data : []).filter(t => t.caseNumber === cNum)
      setThreads(caseThreads)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async e => {
    e.preventDefault()
    e.stopPropagation() // Stop bubbling just in case

    if (!newSubject.trim()) return

    try {
      // Creating with just me for now
      const res = await ChatService.createThread(caseNumber, newSubject, [])

      setThreads([res, ...threads])
      setIsCreating(false)
      setNewSubject('')
      setSelectedThread(res)
    } catch (err) {
      console.error(err)
      alert(`Debug Error: ${err.message}\nStack: ${err.stack}`) // Show stack trace
    }
  }

  if (loading) return <LoadingSpinner />

  if (selectedThread) {
    return (
      <div className="card shadow-sm" style={{ height: '600px' }}>
        <ChatRoom thread={selectedThread} currentUser={user} onBack={() => setSelectedThread(null)} />
      </div>
    )
  }

  return (
    <div className="case-messages-tab mt-4 p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Case Discussions</h5>
        <button className="btn btn-primary btn-sm" onClick={() => setIsCreating(!isCreating)}>
          {isCreating ? 'Cancel' : 'New Discussion'}
        </button>
      </div>

      {isCreating && (
        <div className="card mb-3 p-3 bg-light">
          <h6>Start New Discussion</h6>
          <div className="d-flex">
            <input
              type="text"
              className="form-control mr-2"
              placeholder="Subject (e.g., Medical Records Status)"
              value={newSubject}
              onChange={e => setNewSubject(e.target.value)}
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCreate(e)
                }
              }}
            />
            <button type="button" onClick={handleCreate} className="btn btn-success text-nowrap">
              Start
            </button>
          </div>
        </div>
      )}

      <div className="list-group">
        {threads.length === 0 ? (
          <div className="text-muted p-3 text-center border rounded">No conversations for this case yet.</div>
        ) : (
          threads.map(t => (
            <button
              key={t.id}
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              onClick={() => setSelectedThread(t)}
            >
              <div>
                <div className="fw-bold">{t.subject}</div>
                <small className="text-muted">Last updated: {new Date(t.updatedOn).toLocaleDateString()}</small>
              </div>
              <span className="badge bg-primary rounded-pill">
                <i className="fas fa-chevron-right"></i>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

export default CaseMessagesTab
